import { chain, reconstruct } from "./fourier.js";
import { activeSignal, maxTerms, onChange, reconstructPath, selectSource, setK, state, summary } from "./signal.js";
import { colors, onThemeChange } from "./theme.js";
import { bar, circle, dot, fitter, label, polyline, prepare, segment } from "./plot.js";

/**
 * The "mathematical process" section: every figure and formula is computed from the analysed signal of signal.js.
 */

const NUMBER = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const PHASE = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PERCENT = new Intl.NumberFormat("fr-FR", { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 });
const COMPONENTS = 5; //Components drawn under the signal at step 3.
const TABLE_ROWS = 10;
const SERIES_TERMS = 4; //Terms written in the formula of step 3.
const THUMBNAIL_KS = [1, 2, 3, 5, 10, 20, 50];
const PERIOD = 12000; //Milliseconds for the animated figures to cover t from 0 to 2*pi.
const SHOWN_POWER = 0.995; //The spectrum shows the frequencies carrying this share of the power.

let axis = "x"; //Coordinate decomposed at steps 3 and 4.
let logScale = false;
let time = 0; //Time of the animated figures, in [0, 2*pi).
let spectrumLayout = null; //Geometry of the last drawn spectrum, for the tooltip.
const cache = new Map(); //Reconstructions, keyed by what they depend on.

export function initMathPanel() {
  if (document.getElementById("fx-math") === null) {
    return;
  }
  for (const button of document.querySelectorAll(".fx-source")) {
    button.addEventListener("click", () => selectSource(button.dataset.source));
  }
  for (const radio of document.querySelectorAll('input[name="fx-axis"]')) {
    radio.addEventListener("change", () => {
      axis = radio.value;
      render();
    });
  }
  document.getElementById("fx-log-scale").addEventListener("change", (e) => {
    logScale = e.target.checked;
    drawSpectrum();
  });
  const spectrum = document.getElementById("fx-c-spectrum");
  spectrum.addEventListener("mousemove", (e) => showSpectrumTooltip(e.offsetX, e.offsetY));
  spectrum.addEventListener("mouseleave", () => showSpectrumTooltip(null, null));

  onChange(render);
  onThemeChange(render);
  let resizeTimeout = null;
  window.addEventListener("resize", () => {
    window.clearTimeout(resizeTimeout);
    resizeTimeout = window.setTimeout(render, 150);
  });
  //KaTeX is loaded with defer, it is there once the page is loaded.
  window.addEventListener("load", () => {
    if (window.renderMathInElement) {
      window.renderMathInElement(document.querySelector("main"), {
        delimiters: [
          { left: "\\[", right: "\\]", display: true },
          { left: "\\(", right: "\\)", display: false },
        ],
        throwOnError: false,
      });
    }
    render();
  });
  startAnimation();
}

function render() {
  const signal = activeSignal();
  for (const button of document.querySelectorAll(".fx-source")) {
    const source = state.sources[button.dataset.source];
    button.disabled = source === null;
    button.classList.toggle("active", button.dataset.source === state.active);
    button.setAttribute("aria-pressed", String(button.dataset.source === state.active));
    if (button.dataset.source === "example" && source !== null) {
      button.textContent = "Exemple : " + source.label;
    }
  }
  if (!signal) {
    return;
  }
  const { rms } = summary(signal, state.K);
  document.getElementById("fx-stat-n").textContent = signal.numPoints;
  document.getElementById("fx-stat-freq").textContent = maxTerms(signal);
  document.getElementById("fx-stat-rms").textContent = NUMBER.format(rms) + " px";

  renderSamplesTex(signal);
  renderCoefTex(signal);
  drawSpectrum();
  renderSeriesTex(signal);
  drawComponents(signal);
  renderTable(signal);
  renderErrorTex(signal);
  drawReconstruction(signal);
  renderThumbnails(signal);
  drawAnimated();
}

/* ---------- Helpers ---------- */

function series(signal, coordinate = axis) {
  return coordinate === "x" ? signal.fourierX : signal.fourierY;
}

function samples(signal, coordinate = axis) {
  return coordinate === "x" ? signal.x : signal.y;
}

function memo(key, compute) {
  if (!cache.has(key)) {
    if (cache.size > 64) {
      cache.clear();
    }
    cache.set(key, compute());
  }
  return cache.get(key);
}

const signalIds = new WeakMap();
let nextId = 0;
function idOf(signal) {
  if (!signalIds.has(signal)) {
    signalIds.set(signal, nextId++);
  }
  return signalIds.get(signal);
}

/**
 * @function tex Renders a formula with KaTeX, or shows its source if KaTeX could not be loaded.
 */
function tex(id, source, display = true) {
  const el = document.getElementById(id);
  if (window.katex) {
    window.katex.render(source, el, { displayMode: display, throwOnError: false });
  } else {
    el.textContent = source;
  }
}

/** Number written in LaTeX with a decimal comma. */
function texNumber(value, digits = 1) {
  return value.toFixed(digits).replace(".", "{,}");
}

/** Signed number written in LaTeX, to follow another term: "+ 1{,}2" or "- 1{,}2". */
function texSigned(value, digits = 1) {
  return (value < 0 ? " - " : " + ") + Math.abs(value).toFixed(digits).replace(".", "{,}");
}

function texComplex(re, im) {
  return texNumber(re) + texSigned(im) + "\\,i";
}

function texCosine(term) {
  const k = term.freq === 1 ? "" : term.freq;
  return `${texNumber(term.amp)}\\cos(${k}t${texSigned(term.phase, 2)})`;
}

function complexText(re, im) {
  return `${NUMBER.format(re)} ${im < 0 ? "−" : "+"} ${NUMBER.format(Math.abs(im))} i`;
}

/* ---------- Step 1: sampling ---------- */

function renderSamplesTex(signal) {
  const N = signal.numPoints;
  const line = (name, values) =>
    `${name}_0 = ${texNumber(values[0])},\\; ${name}_1 = ${texNumber(values[1])},\\; ${name}_2 = ${texNumber(values[2])},\\; \\dots,\\; ${name}_{${N - 1}} = ${texNumber(values[N - 1])}`;
  tex("fx-tex-samples", `\\begin{gathered} N = ${N} \\\\ ${line("x", signal.x)} \\\\ ${line("y", signal.y)} \\end{gathered}`);
}

function drawSampling(signal) {
  const canvas = document.getElementById("fx-c-sampling");
  const size = Math.min(canvas.clientWidth, 280);
  const { ctx, width, height } = prepare(canvas, size);
  const color = colors();
  const map = fitter(signal.x, signal.y, { left: 16, top: 16, width: width - 32, height: height - 32 });
  const points = signal.x.map((x, i) => map(x, signal.y[i]));

  polyline(ctx, points, color.faint, 1, true);
  const radius = signal.numPoints > 400 ? 1 : 1.5;
  for (const p of points) {
    dot(ctx, p.x, p.y, radius, color["ink-muted"]);
  }
  label(ctx, "P₀", points[0].x + 6, points[0].y - 8, color.ink);

  //The current point and its two coordinates, read on the bottom and left edges.
  const p = points[Math.floor((time / (2 * Math.PI)) * signal.numPoints) % signal.numPoints];
  segment(ctx, p.x, p.y, p.x, height, color.x, 1);
  segment(ctx, p.x, p.y, 0, p.y, color.y, 1);
  dot(ctx, p.x, height - 4, 4, color.x);
  dot(ctx, 4, p.y, 4, color.y);
  dot(ctx, p.x, p.y, 5, color.ink, color.surface);
  label(ctx, "P(t)", p.x + 9, p.y + 10, color.ink);
}

function drawSignals(signal) {
  const canvas = document.getElementById("fx-c-signals");
  const sampling = document.getElementById("fx-c-sampling");
  const { ctx, width, height } = prepare(canvas, Math.min(sampling.clientWidth, 280));
  const color = colors();
  const left = 46;
  const right = width - 10;
  const plotHeight = (height - 34) / 2;
  const n = Math.floor((time / (2 * Math.PI)) * signal.numPoints) % signal.numPoints;
  const cursor = left + (n / signal.numPoints) * (right - left);

  for (const [row, coordinate] of [[0, "x"], [1, "y"]]) {
    const values = samples(signal, coordinate);
    const top = 6 + row * (plotHeight + 12);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const toY = (v) => top + plotHeight - ((v - min) / (max - min || 1)) * plotHeight;
    const toX = (i) => left + (i / values.length) * (right - left);

    segment(ctx, left, top + plotHeight, right, top + plotHeight, color.grid);
    segment(ctx, left, top, left, top + plotHeight, color.grid);
    label(ctx, NUMBER.format(max), left - 6, top + 4, color["ink-muted"], "right");
    label(ctx, NUMBER.format(min), left - 6, top + plotHeight - 4, color["ink-muted"], "right");
    polyline(ctx, values.map((v, i) => ({ x: toX(i), y: toY(v) })), color[coordinate], 2);
    if (values.length <= 120) {
      for (let i = 0; i < values.length; i++) {
        dot(ctx, toX(i), toY(values[i]), 2, color[coordinate]);
      }
    }
    label(ctx, coordinate + "(t)", right, top + 8, color.ink, "right");
    dot(ctx, cursor, toY(values[n]), 4, color[coordinate], color.surface);
  }
  segment(ctx, cursor, 6, cursor, height - 22, color["ink-muted"]);
  const axisY = height - 12;
  label(ctx, "0", left, axisY, color["ink-muted"], "center");
  label(ctx, "π", (left + right) / 2, axisY, color["ink-muted"], "center");
  label(ctx, "2π", right, axisY, color["ink-muted"], "right");
}

/* ---------- Step 2: discrete Fourier transform ---------- */

function renderCoefTex(signal) {
  const s = series(signal);
  const top = s.terms[0];
  const k = top.freq;
  tex(
    "fx-tex-coef",
    `\\begin{gathered} \\text{Pour } ${axis}(t) : \\quad a_0 = c_0 = ${texNumber(s.mean)} \\\\ ` +
      `c_{${k}} = ${texComplex(top.re, top.im)} \\;\\Rightarrow\\; A_{${k}} = ${texNumber(top.amp)}, \\;\\; \\varphi_{${k}} = ${texNumber(top.phase, 2)} \\end{gathered}`,
  );
}

/** For each coordinate, the rank of every frequency in the sorted terms. */
function ranks(signal) {
  return memo(`ranks:${idOf(signal)}`, () => {
    const result = {};
    for (const coordinate of ["x", "y"]) {
      const rank = [];
      series(signal, coordinate).terms.forEach((term, i) => (rank[term.freq] = i));
      result[coordinate] = rank;
    }
    return result;
  });
}

/** Highest frequency shown by the spectrum: the lower frequencies carry SHOWN_POWER of the power of both coordinates. */
function shownFrequencies(signal) {
  const max = maxTerms(signal);
  let shown = 0;
  for (const coordinate of ["x", "y"]) {
    const s = series(signal, coordinate);
    const byFreq = [...s.terms].sort((a, b) => a.freq - b.freq);
    let power = 0;
    for (const term of byFreq) {
      power += term.power;
      if (power >= SHOWN_POWER * s.totalPower) {
        shown = Math.max(shown, term.freq);
        break;
      }
    }
  }
  return Math.min(max, Math.max(shown, 16));
}

function drawSpectrum() {
  const signal = activeSignal();
  if (!signal) {
    return;
  }
  const canvas = document.getElementById("fx-c-spectrum");
  const { ctx, width, height } = prepare(canvas, 280);
  const color = colors();
  const rank = ranks(signal);
  const shown = shownFrequencies(signal);
  const left = 48;
  const right = width - 8;
  const mid = (height - 24) / 2 + 4;
  const half = mid - 8;
  const slot = (right - left) / shown;
  const barWidth = Math.max(1, Math.min(24, slot * 0.7));

  const amplitudes = { x: [], y: [] };
  let maxAmp = 0;
  for (const coordinate of ["x", "y"]) {
    for (const term of series(signal, coordinate).terms) {
      amplitudes[coordinate][term.freq] = term.amp;
      maxAmp = Math.max(maxAmp, term.amp);
    }
  }
  //Linear scale, or logarithmic over 4 decades.
  const floor = Math.log10(maxAmp || 1) - 4;
  const scale = (a) =>
    logScale ? Math.max(0, (Math.log10(a || 1e-12) - floor) / (Math.log10(maxAmp || 1) - floor)) : a / (maxAmp || 1);

  segment(ctx, left, mid, right, mid, color.grid);
  for (let k = 1; k <= shown; k++) {
    const x = left + (k - 0.5) * slot - barWidth / 2;
    for (const [coordinate, sign] of [["x", 1], ["y", -1]]) {
      const selected = rank[coordinate][k] < state.K;
      const highlighted = spectrumLayout && spectrumLayout.hover === k;
      const fill = selected || highlighted ? color[coordinate] : color.faint;
      const h = scale(amplitudes[coordinate][k]) * half;
      if (h > 0.5) {
        bar(ctx, x, sign > 0 ? mid - 1 : mid + 1, barWidth, sign * h, fill);
      }
    }
  }

  //Amplitude ticks and frequency axis.
  label(ctx, NUMBER.format(maxAmp), left - 6, mid - half, color["ink-muted"], "right");
  label(ctx, logScale ? String(Number(Math.pow(10, floor).toPrecision(1))).replace(".", ",") : "0", left - 6, mid, color["ink-muted"], "right");
  label(ctx, NUMBER.format(maxAmp), left - 6, mid + half, color["ink-muted"], "right");
  const step = [1, 2, 5, 10, 20, 50, 100, 200, 500].find((s) => s * slot >= 28) || 1000;
  for (let k = step; k <= shown; k += step) {
    label(ctx, String(k), left + (k - 0.5) * slot, height - 10, color["ink-muted"], "center");
  }
  label(ctx, "k", left - 6, height - 10, color["ink-muted"], "right");
  spectrumLayout = { ...(spectrumLayout || {}), left, slot, shown };

  const { K, max } = summary(signal, state.K);
  document.getElementById("fx-spectrum-caption").textContent =
    `Amplitudes Aₖ pour k = 1 à ${shown}` +
    (shown < max ? ` (sur ${max} : les fréquences suivantes portent moins de 0,5 % de l'énergie)` : "") +
    `. En couleur, les ${K} plus grands termes de chaque coordonnée, conservés par l'animation.`;
}

function showSpectrumTooltip(mouseX, mouseY) {
  const tooltip = document.getElementById("fx-spectrum-tooltip");
  const signal = activeSignal();
  let k = null;
  if (mouseX !== null && signal && spectrumLayout) {
    k = Math.floor((mouseX - spectrumLayout.left) / spectrumLayout.slot) + 1;
    if (k < 1 || k > spectrumLayout.shown) {
      k = null;
    }
  }
  if (spectrumLayout && k !== spectrumLayout.hover) {
    spectrumLayout.hover = k;
    drawSpectrum();
  }
  if (k === null) {
    tooltip.hidden = true;
    return;
  }
  const rank = ranks(signal);
  const lines = [`<strong>k = ${k}</strong>`];
  for (const coordinate of ["x", "y"]) {
    const term = series(signal, coordinate).terms[rank[coordinate][k]];
    lines.push(
      `<span class="fx-key fx-key-${coordinate}"></span>${coordinate}(t) : A = ${NUMBER.format(term.amp)}, φ = ${PHASE.format(term.phase)}, rang ${rank[coordinate][k] + 1}`,
    );
  }
  tooltip.innerHTML = lines.join("<br>");
  tooltip.hidden = false;
  const figure = tooltip.parentElement;
  const x = Math.min(mouseX + 16, figure.clientWidth - tooltip.offsetWidth - 4);
  tooltip.style.left = Math.max(4, x) + "px";
  tooltip.style.top = Math.max(4, mouseY - tooltip.offsetHeight - 8) + "px";
}

/* ---------- Step 3: decomposition into sinusoids ---------- */

function renderSeriesTex(signal) {
  const s = series(signal);
  const terms = s.terms.slice(0, SERIES_TERMS).map(texCosine).join(" + ");
  const more = s.terms.length > SERIES_TERMS ? ` + \\cdots \\quad (${s.terms.length}\\ \\text{termes})` : "";
  tex("fx-tex-series", `${axis}(t) = ${texNumber(s.mean)} + ${terms}${more}`);
}

function drawComponents(signal) {
  const canvas = document.getElementById("fx-c-components");
  const color = colors();
  const s = series(signal);
  const values = samples(signal);
  const terms = s.terms.slice(0, COMPONENTS);
  const deviation = Math.max(...values.map((v) => Math.abs(v - s.mean)));

  //Every row gets the height of its own curve at a common scale (with a minimum), so amplitudes compare directly.
  const rows = [{ name: `${axis}(t) − a₀`, amp: deviation }].concat(
    terms.map((term) => ({ name: `k = ${term.freq} · A = ${NUMBER.format(term.amp)}`, amp: term.amp, term })),
  );
  const available = 360 - rows.length * 10;
  const total = rows.reduce((sum, row) => sum + 2 * row.amp, 0);
  const scale = available / (total || 1);
  const heights = rows.map((row) => Math.max(26, 2 * row.amp * scale + 10));
  const { ctx, width, height } = prepare(canvas, heights.reduce((a, b) => a + b, 0) + 22);

  const left = Math.min(150, width * 0.32);
  const right = width - 10;
  const numSamples = Math.min(Math.max(4 * terms.reduce((m, t) => Math.max(m, t.freq), 1), 200), 1200);
  const toX = (t) => left + (t / (2 * Math.PI)) * (right - left);
  let top = 0;
  rows.forEach((row, i) => {
    const middle = top + heights[i] / 2;
    segment(ctx, left, middle, right, middle, color.grid);
    label(ctx, row.name, 8, middle, i === 0 ? color.ink : color["ink-muted"]);
    let points;
    if (row.term) {
      points = [];
      for (let j = 0; j <= numSamples; j++) {
        const t = (2 * Math.PI * j) / numSamples;
        points.push({ x: toX(t), y: middle - row.amp * scale * Math.cos(row.term.freq * t + row.term.phase) });
      }
    } else {
      points = values.map((v, j) => ({ x: toX((2 * Math.PI * j) / values.length), y: middle - (v - s.mean) * scale }));
    }
    polyline(ctx, points, i === 0 ? color.ink : color[axis], i === 0 ? 2 : 1.5);
    top += heights[i];
  });
  label(ctx, "0", left, height - 10, color["ink-muted"], "center");
  label(ctx, "π", (left + right) / 2, height - 10, color["ink-muted"], "center");
  label(ctx, "2π", right, height - 10, color["ink-muted"], "right");
}

function renderTable(signal) {
  const s = series(signal);
  const body = document.getElementById("fx-table-body");
  body.textContent = "";
  s.terms.slice(0, TABLE_ROWS).forEach((term, i) => {
    const row = document.createElement("tr");
    row.classList.toggle("fx-dropped", i >= state.K);
    const cells = [
      i + 1,
      term.freq,
      complexText(term.re, term.im),
      NUMBER.format(term.amp),
      PHASE.format(term.phase),
      PERCENT.format(s.totalPower > 0 ? term.power / s.totalPower : 0),
    ];
    for (const value of cells) {
      const cell = document.createElement("td");
      cell.textContent = value;
      row.appendChild(cell);
    }
    body.appendChild(row);
  });
  const dropped = state.K < TABLE_ROWS ? " Les termes au-delà de K, écartés par l'animation, sont estompés." : "";
  document.getElementById("fx-table-caption").textContent =
    `Les ${Math.min(TABLE_ROWS, s.terms.length)} plus grands termes de ${axis}(t), sur ${s.terms.length}. ` +
    `Énergie : part de la variance du signal portée par le terme.${dropped}`;
}

/* ---------- Step 4: from sinusoids to epicycles ---------- */

function drawChain(signal) {
  const canvas = document.getElementById("fx-c-chain");
  const { ctx, width, height } = prepare(canvas, 300);
  const color = colors();
  const s = series(signal);
  const values = samples(signal);
  const K = state.K;
  const count = Math.min(K, s.terms.length);

  const chainWidth = Math.min(width * 0.38, 300);
  const cx = chainWidth / 2;
  const cy = height / 2;
  const deviation = Math.max(...values.map((v) => Math.abs(v - s.mean)));
  const biggest = s.terms.length ? s.terms[0].amp : 1;
  const scale = Math.min((height / 2 - 14) / Math.max(deviation, biggest), (chainWidth / 2 - 8) / biggest);
  const left = chainWidth + 16;
  const right = width - 10;
  const toX = (t) => left + (t / (2 * Math.PI)) * (right - left);

  //The wave traced by the tip of the chain: the series truncated to its K largest terms.
  const numSamples = Math.min(Math.max(2 * signal.numPoints, 400), 1500);
  const wave = memo(`wave:${idOf(signal)}:${axis}:${K}`, () => reconstruct(s, K, numSamples));

  segment(ctx, left, cy, right, cy, color.grid);
  label(ctx, "a₀", right, cy - 8, color["ink-muted"], "right");
  polyline(ctx, values.map((v, j) => ({ x: toX((2 * Math.PI * j) / values.length), y: cy - (v - s.mean) * scale })), color.faint, 2);
  const done = Math.floor((time / (2 * Math.PI)) * numSamples);
  polyline(ctx, wave.slice(0, done + 1).map((v, j) => ({ x: toX((2 * Math.PI * j) / numSamples), y: cy - (v - s.mean) * scale })), color[axis], 2);

  //Chain turned by -pi/2: the height of its tip is the value of the series.
  const joints = chain(s.terms, K, time, cx, cy, -Math.PI / 2, scale);
  for (let i = 0; i < joints.length - 1; i++) {
    if (joints[i].r > 0.5) {
      circle(ctx, joints[i].x, joints[i].y, joints[i].r, color.faint);
    }
    segment(ctx, joints[i].x, joints[i].y, joints[i + 1].x, joints[i + 1].y, color["ink-muted"]);
  }
  const tip = joints[joints.length - 1];
  const waveX = toX(time);
  segment(ctx, tip.x, tip.y, waveX, tip.y, color["ink-muted"]);
  dot(ctx, tip.x, tip.y, 4, color[axis], color.surface);
  dot(ctx, waveX, tip.y, 4, color[axis], color.surface);

  document.getElementById("fx-chain-caption").textContent =
    `Chaîne des ${count} plus grands termes de ${axis}(t) : la hauteur de son extrémité trace la somme (en couleur). ` +
    "En gris, le signal d'origine.";
}

/* ---------- Step 5: progressive reconstruction ---------- */

function renderErrorTex(signal) {
  const { power, rms } = summary(signal, state.K);
  const percent = (100 * power).toFixed(1).replace(".", "{,}");
  tex(
    "fx-tex-error",
    `\\begin{gathered} \\frac{\\sum_{\\text{conservés}} |c_k|^2}{\\sum_{k} |c_k|^2} = ${percent}\\,\\% \\quad (x \\text{ et } y) \\\\ ` +
      `\\sqrt{\\frac{1}{N} \\sum_{n} \\left\\| P_n - P_K(t_n) \\right\\|^2} = ${texNumber(rms)}\\ \\text{px} \\end{gathered}`,
  );
}

function drawShape(canvas, signal, K, height, numSamples) {
  const { ctx, width } = prepare(canvas, height);
  const color = colors();
  //The reconstructions can overshoot the original a little: keep some room.
  const map = fitter(signal.x, signal.y, { left: width * 0.08, top: height * 0.08, width: width * 0.84, height: height * 0.84 });
  const radius = signal.numPoints > 400 || height < 200 ? 1 : 1.5;
  for (let i = 0; i < signal.numPoints; i++) {
    const p = map(signal.x[i], signal.y[i]);
    dot(ctx, p.x, p.y, radius, color.faint);
  }
  const path = memo(`path:${idOf(signal)}:${K}:${numSamples}`, () => reconstructPath(signal, K, numSamples));
  polyline(ctx, path.map((p) => map(p.x, p.y)), color.ink, height < 200 ? 1.5 : 2, true);
}

function drawReconstruction(signal) {
  const canvas = document.getElementById("fx-c-reconstruction");
  drawShape(canvas, signal, state.K, 320, Math.min(Math.max(2 * signal.numPoints, 400), 2000));
}

function renderThumbnails(signal) {
  const container = document.getElementById("fx-thumbs");
  const max = maxTerms(signal);
  const ks = THUMBNAIL_KS.filter((K) => K < max).concat([Infinity]);
  const key = `${idOf(signal)}:${container.clientWidth}:${document.body.className}`;
  if (container.dataset.key !== key) {
    container.dataset.key = key;
    container.textContent = "";
    for (const K of ks) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "fx-thumb";
      button.dataset.k = String(K);
      const canvas = document.createElement("canvas");
      canvas.className = "fx-plot";
      const caption = document.createElement("span");
      caption.innerHTML = `<strong>K = ${K === Infinity ? max : K}</strong><br>${PERCENT.format(summary(signal, K).power)}`;
      button.append(canvas, caption);
      button.addEventListener("click", () => setK(K));
      container.appendChild(button);
      drawShape(canvas, signal, K, 110, 300);
    }
  }
  for (const button of container.children) {
    const K = Number(button.dataset.k);
    const active = K === state.K || (K === Infinity && state.K >= max);
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
  }
}

/* ---------- Animation of steps 1 and 4 ---------- */

function drawAnimated() {
  const signal = activeSignal();
  if (!signal) {
    return;
  }
  drawSampling(signal);
  drawSignals(signal);
  drawChain(signal);
}

function startAnimation() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    time = Math.PI / 3; //Still figures, frozen at an arbitrary time.
    return;
  }
  const visible = new Set();
  let running = false;
  let last = 0;
  const tick = (now) => {
    if (visible.size === 0) {
      running = false;
      return;
    }
    time = (time + (2 * Math.PI * (now - last)) / PERIOD) % (2 * Math.PI);
    last = now;
    drawAnimated();
    window.requestAnimationFrame(tick);
  };
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        visible.add(entry.target);
      } else {
        visible.delete(entry.target);
      }
    }
    if (visible.size > 0 && !running) {
      running = true;
      last = performance.now();
      window.requestAnimationFrame(tick);
    }
  });
  for (const id of ["fx-c-sampling", "fx-c-signals", "fx-c-chain"]) {
    observer.observe(document.getElementById(id));
  }
}
